import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Send, Loader2, ShieldCheck, Camera, ExternalLink, ChevronRight } from "lucide-react";
import jsQR from "jsqr";

const readQr = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    let found = null;
    for (const size of [1200, 800, 1600, 500]) {
      const s = Math.min(1, size / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * s); canvas.height = Math.round(img.height * s);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const d = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const r = jsQR(d.data, d.width, d.height, { inversionAttempts: "attemptBoth" });
      if (r?.data) { found = r.data; break; }
    }
    URL.revokeObjectURL(url);
    resolve(found);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
  img.src = url;
});

const fmt = (t) => t
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/\*(.*?)\*/g, "<b>$1</b>")
  .replace(/(^|\s)_(.+?)_(?=\s|$)/g, "$1<i>$2</i>");
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");
const MENU = [
  ["📊 My balance", "📊 Balance", "📊 बैलेंस"],
  ["⚡ Redeem", "⚡ Redeem", "⚡ रिडीम"],
  ["📷 Scan coupon", "📷 Scan", "📷 स्कैन"],
  ["help", "💬 Help", "💬 सहायता"],
];

const Bubble = ({ m, i }) => (
  <div data-testid={`agent-chat-msg-${i}`} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-1 duration-300`}>
    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[15px] leading-snug shadow-sm ${m.from === "user" ? "bg-[#8B0000] text-white rounded-br-md" : "bg-white text-gray-800 rounded-bl-md"}`}>
      <div className="whitespace-pre-line break-words" dangerouslySetInnerHTML={{ __html: fmt(m.text || "") }} />
      <div className={`text-[10px] text-right mt-1 ${m.from === "user" ? "text-white/60" : "text-gray-400"}`}>{time(m.at)}</div>
    </div>
  </div>
);

const Actions = ({ m, onSend, onLink }) => (
  <div className="px-3 pb-2 space-y-2" data-testid="agent-chat-actions">
    {m.link && (
      <button data-testid="agent-chat-link-btn" onClick={() => onLink(m.link.url)}
        className="w-full flex items-center justify-center gap-2 bg-[#8B0000] text-white font-bold py-3 rounded-full shadow active:scale-[0.98] transition-transform">
        {m.link.label} <ExternalLink className="h-4 w-4" />
      </button>
    )}
    {m.rows?.length > 0 && (
      <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100 overflow-hidden" data-testid="agent-chat-list">
        {m.rows.map((r, i) => (
          <button key={r.id} data-testid={`agent-chat-row-${i}`} onClick={() => onSend(r.title, r.id)}
            className="w-full flex items-center justify-between text-left px-4 py-3 hover:bg-[#FDF2F2] transition-colors">
            <span><span className="block text-sm font-semibold text-gray-900">{r.title}</span>{r.description && <span className="block text-xs text-gray-500">{r.description}</span>}</span>
            <ChevronRight className="h-4 w-4 text-gray-400" />
          </button>
        ))}
      </div>
    )}
    {m.buttons?.length > 0 && (
      <div className="flex flex-wrap gap-2" data-testid="agent-chat-quick-replies">
        {m.buttons.map((b, i) => (
          <button key={b} data-testid={`agent-chat-quick-${i}`} onClick={() => onSend(b, null, m.button_labels?.[i])}
            className="bg-white text-[#8B0000] border border-[#8B0000]/25 font-semibold text-sm px-4 py-2 rounded-full shadow-sm hover:bg-[#8B0000] hover:text-white active:scale-95 transition-[background-color,color,transform]">{m.button_labels?.[i] || b}</button>
        ))}
      </div>
    )}
  </div>
);

export default function AgentChat() {
  const { token } = useParams();
  const nav = useNavigate();
  const [chat, setChat] = useState(null);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const load = () => api.get(`/agent/chat/${token}`).then((r) => setChat(r.data)).catch((e) => setError(apiErr(e)));
    load();
    const t = setInterval(() => { if (!document.hidden) api.get(`/agent/chat/${token}`).then((r) => setChat((c) => (c && r.data.messages.length > c.messages.length ? r.data : c))).catch(() => {}); }, 8000);
    return () => clearInterval(t);
  }, [token]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat?.messages?.length, busy]);

  const push = (msgs) => setChat((c) => ({ ...c, messages: [...c.messages.filter((m) => !m.pending), ...msgs] }));
  const run = async (req, optimistic) => {
    setBusy(true);
    if (optimistic) setChat((c) => ({ ...c, messages: [...c.messages, { from: "user", text: optimistic, at: new Date().toISOString(), pending: true }] }));
    try { const { data } = await req(); push(data.messages); }
    catch (e) { push([{ from: "bot", text: `⚠️ ${apiErr(e)}`, at: new Date().toISOString() }]); }
    finally { setBusy(false); }
  };
  const send = (value, replyId, shownLabel) => {
    const v = (value ?? text).trim();
    if (!v || busy) return;
    setText("");
    run(() => api.post(`/agent/chat/${token}/message`, { text: v, reply_id: replyId || null }), shownLabel || v);
  };
  const setLang = async (lang) => {
    try { const { data } = await api.post(`/agent/chat/${token}/lang`, { lang }); setChat(data); } catch (e) { push([{ from: "bot", text: `⚠️ ${apiErr(e)}`, at: new Date().toISOString() }]); }
  };
  const photo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const decoded = await readQr(f);
    setBusy(false);
    if (decoded) {
      run(() => api.post(`/agent/chat/${token}/message`, { text: decoded.slice(0, 480), reply_id: null, qr: true }), "📷 Photo of QR code");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    run(() => api.post(`/agent/chat/${token}/photo`, fd), "📷 Photo of QR code");
  };
  const openLink = (url) => {
    try { const u = new URL(url); if (u.origin === window.location.origin) { nav(u.pathname + u.search); return; } } catch { /* external */ }
    window.open(url, "_blank", "noopener");
  };

  const msgs = chat?.messages || [];
  const last = msgs[msgs.length - 1];
  const active = chat?.status === "active";
  const showActions = !busy && active && last?.from === "bot";

  return (
    <div className="min-h-[100dvh] bg-[#1F1A17] flex justify-center">
      <div className="w-full max-w-md flex flex-col h-[100dvh] bg-[#F6F1EA]" data-testid="agent-chat">
        <header className="bg-[#8B0000] text-white px-4 py-3 flex items-center gap-3 shadow">
          <img src="https://jewellpaints.com/wp-content/uploads/2026/03/Jewell-paints-logo-1.png" alt="" className="h-9 w-9 rounded-full bg-white object-contain p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold">Jewel Paints Rewards</div>
            <div className="text-[11px] text-white/75 flex items-center gap-1" data-testid="agent-chat-identity">
              <ShieldCheck className="h-3 w-3" /> {chat ? `Secure session · ${chat.mobile_masked}` : error ? "Link unavailable" : "Connecting…"}
            </div>
          </div>
          {chat && (
            <div className="flex rounded-full bg-white/15 p-0.5 text-xs font-bold" data-testid="agent-chat-lang-toggle">
              {[["en", "EN"], ["hi", "हिं"]].map(([k, l]) => (
                <button key={k} data-testid={`agent-chat-lang-${k}`} onClick={() => setLang(k)}
                  className={`px-2.5 py-1 rounded-full ${(chat.lang || "en") === k ? "bg-white text-[#8B0000]" : "text-white/80"}`}>{l}</button>
              ))}
            </div>
          )}
        </header>
        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="agent-chat-messages">
          {!chat && !error && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#8B0000]" /></div>}
          {error && <div data-testid="agent-chat-error" className="mx-auto mt-10 max-w-xs text-center bg-white rounded-2xl p-5 text-sm text-gray-700 shadow">{error}. Send <b>Hi</b> on WhatsApp to get a new link.</div>}
          {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
          {busy && <div className="flex justify-start"><div className="bg-white rounded-2xl px-3 py-2 shadow-sm"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div></div>}
          <div ref={endRef} />
        </div>
        {chat && !active && <div data-testid="agent-chat-ended-banner" className="mx-3 mb-2 rounded-xl bg-[#FFF3C4] text-[#5C4A00] text-sm text-center px-3 py-2">This chat has expired. Send Hi on WhatsApp to get a new link.</div>}
        {showActions && <Actions m={last} onSend={send} onLink={openLink} />}
        {active && (
          <div className="px-3 pb-2 flex gap-2 overflow-x-auto" data-testid="agent-chat-menu">
            {MENU.map(([value, en, hi], i) => (
              <button key={value} data-testid={`agent-chat-menu-${i}`} disabled={busy} onClick={() => send(value, null, chat.lang === "hi" ? hi : en)}
                className="shrink-0 text-xs font-semibold text-gray-700 bg-white/80 border border-gray-200 px-3 py-1.5 rounded-full hover:border-[#8B0000] hover:text-[#8B0000] disabled:opacity-50">{chat.lang === "hi" ? hi : en}</button>
            ))}
          </div>
        )}
        <div className="bg-white border-t border-gray-200 px-3 py-2 flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={photo} data-testid="agent-chat-photo-input" />
          <button data-testid="agent-chat-photo-btn" disabled={!active || busy} onClick={() => fileRef.current?.click()} title="Send a photo of the QR code"
            className="h-10 w-10 rounded-full text-[#8B0000] flex items-center justify-center hover:bg-[#FDF2F2] disabled:opacity-40"><Camera className="h-5 w-5" /></button>
          <input data-testid="agent-chat-input" value={text} disabled={!active || busy}
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={active ? "Type a message or coupon code…" : "Chat expired"}
            className="flex-1 bg-[#F3EEE8] rounded-full px-4 py-2.5 text-sm outline-none disabled:opacity-60" />
          <button data-testid="agent-chat-send-btn" onClick={() => send()} disabled={!text.trim() || busy || !active}
            className="h-10 w-10 rounded-full bg-[#8B0000] text-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
